import React, { useEffect, useMemo, useState } from 'react';
import './UpgradeModal.css';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { IconCheck, IconInfo } from './Icons';
import { useBrandIdentity } from '../utils/brandIdentity';
import { AI_MONTHLY_QUESTIONS, PRO_TRIAL_DAYS, proPriceFor } from '../utils/proPricing';

const PLANS = {
  basic: {
    id: 'basic',
    name: 'Basic',
    tagline: 'Great way to start',
    price: '$0',
    period: '/ Month',
    currentLabel: 'Current plan',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    tagline: 'Unlock all risk tools',
    period: '/ Month',
    trialLabel: `Free ${PRO_TRIAL_DAYS}-Day Trial`,
  },
};

const BASIC_FEATURES = [
  { label: 'Overview dashboard (view only)', info: 'Read-only org-wide performance overview.' },
  { label: 'Strategy results — current & summary views', info: 'Calendar, chart, and spreadsheet views for strategy history.' },
  { label: 'Risk tools sample previews', info: 'See what Blowout Risk and % Rule look like before upgrading.' },
];

const PRO_FEATURES = [
  { label: 'RiskLo AI', info: `Ask about your own accounts, trades, the market calendar and prop firm rules. ${AI_MONTHLY_QUESTIONS.pro} questions a month.`, badge: 'New' },
  { label: 'My Portfolio', info: 'Link brokerages, track equity, returns, open positions, and reconciliation.' },
  { label: 'Portfolio Simulator', info: 'Combine strategies by weekday and model worst days before going live.' },
  { label: 'Blowout Risk & % Rule Risk', info: 'GO / NO GO reads, drawdown probability, and consistency checks.' },
  { label: 'Bulk Risk Calculator', info: 'Run blowout and rule checks across multiple accounts at once.' },
  { label: 'Custom Dashboards', info: 'Up to 5 personalized views with KPIs, calendars, and charts.' },
  { label: 'Custom Strat Combos', info: 'Mix strategies by weekday into your own multi-strat setups.' },
  { label: 'Detailed KPI metrics', info: 'Full stats on the results page — not blurred.' },
  { label: 'Compare Setups', info: 'Side-by-side strategy analysis including standard deviation.' },
  { label: 'Upload & Analyze', info: 'Run deeper risk analysis on NinjaTrader exports.' },
  { label: 'Risk Summaries', info: 'Automated email digests so you stay consistent.' },
];

const DEFAULT_SUBTITLE = 'Choose the best plan for your trading workflow.';

function PlanFeatureRow({ label, info, badge }) {
  return (
    <li className="upgrade-plan-feature">
      <span className="upgrade-plan-feature-info" title={info} aria-label={info}>
        <IconInfo size={14} color="rgba(148, 163, 184, 0.9)" />
      </span>
      <span className="upgrade-plan-feature-label">
        {label}
        {badge ? <span className="upgrade-modal-beta-badge">{badge}</span> : null}
      </span>
      <span className="upgrade-plan-feature-check" aria-hidden="true">
        <IconCheck size={16} color="#22c55e" />
      </span>
    </li>
  );
}

function UpgradeModal({ isOpen, onClose, user, isPro, customMessage }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const showPricingComingSoon = process.env.REACT_APP_SHOW_PRICING_COMING_SOON === 'true';
  const showCheckoutOptions = !showPricingComingSoon;

  // Partner brands with their own checkout: upgrades go to the brand first.
  const identity = useBrandIdentity();
  const brandCheckout = !isPro && identity.plan === 'partner' && identity.checkoutUrl ? identity.checkoutUrl : null;
  const [showRiskLoPlans, setShowRiskLoPlans] = useState(false);
  useEffect(() => { if (isOpen) setShowRiskLoPlans(false); }, [isOpen]);

  const activePlan = PLANS[selectedPlan] || PLANS.pro;
  const featureList = selectedPlan === 'pro' ? PRO_FEATURES : BASIC_FEATURES;

  useEffect(() => {
    const resetLoadingState = () => setLoading(false);
    window.addEventListener('pageshow', resetLoadingState);
    window.addEventListener('focus', resetLoadingState);
    return () => {
      window.removeEventListener('pageshow', resetLoadingState);
      window.removeEventListener('focus', resetLoadingState);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSelectedPlan('pro');
      setError(null);
    }
  }, [isOpen]);

  const trialEndNote = useMemo(() => {
    const end = new Date();
    end.setDate(end.getDate() + 7);
    return end.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }, []);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    if (isPro) {
      setError('You already have RiskLo Pro!');
      return;
    }
    if (selectedPlan !== 'pro') {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const healthCheck = await fetch(API_ENDPOINTS.health);
      if (!healthCheck.ok) {
        throw new Error(`Backend health check failed: ${healthCheck.status} ${healthCheck.statusText}`);
      }

      const response = await fetch(API_ENDPOINTS.subscriptionCreateCheckout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBearerAuthHeaders(),
        },
        body: JSON.stringify({
          email: user?.email || '',
          useVectorCoupon: false,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error(`Server error: Received ${response.status} ${response.statusText}.`);
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to create checkout session');
      }
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err.message || 'Failed to create checkout session. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content upgrade-plan-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>

        {brandCheckout && !showRiskLoPlans ? (
          <div className="upgrade-brand">
            {identity.logoUrl ? <img src={identity.logoUrl} alt="" className="upgrade-brand-logo" /> : null}
            <h2 className="upgrade-plan-title">Join {identity.appName}</h2>
            <p className="upgrade-plan-subtitle">
              {customMessage ? `${customMessage} ` : ''}Your {identity.appName} membership unlocks the full app, including the risk tools, your portfolio and AI.
            </p>
            <a className="upgrade-button upgrade-brand-cta" href={brandCheckout} target="_blank" rel="noopener noreferrer">
              Continue to {identity.appName} checkout
            </a>
            <button type="button" className="upgrade-brand-alt" onClick={() => setShowRiskLoPlans(true)}>
              Prefer a RiskLo plan on its own? See RiskLo plans
            </button>
            <p className="upgrade-brand-powered">Powered by RiskLo</p>
          </div>
        ) : null}

        {brandCheckout && !showRiskLoPlans ? null : (
        <>
        <div className="upgrade-plan-header">
          <h2 className="upgrade-plan-title">Choose a Plan</h2>
          <p className="upgrade-plan-subtitle">{customMessage || DEFAULT_SUBTITLE}</p>
        </div>

        {showCheckoutOptions ? (
          <>
            <div className="upgrade-plan-body">
              <div className="upgrade-plan-options" role="radiogroup" aria-label="Subscription plan">
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedPlan === 'basic'}
                  className={`upgrade-plan-card ${selectedPlan === 'basic' ? 'is-selected' : ''}`}
                  onClick={() => setSelectedPlan('basic')}
                >
                  <span className="upgrade-plan-radio" aria-hidden="true" />
                  <div className="upgrade-plan-card-main">
                    <div className="upgrade-plan-card-top">
                      <span className="upgrade-plan-name">{PLANS.basic.name}</span>
                      {!isPro ? (
                        <span className="upgrade-plan-pill upgrade-plan-pill--current">{PLANS.basic.currentLabel}</span>
                      ) : null}
                    </div>
                    <span className="upgrade-plan-tagline">{PLANS.basic.tagline}</span>
                  </div>
                  <div className="upgrade-plan-price">
                    <span>{PLANS.basic.price}</span>
                    <span className="upgrade-plan-period">{PLANS.basic.period}</span>
                  </div>
                </button>

                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedPlan === 'pro'}
                  className={`upgrade-plan-card upgrade-plan-card--pro ${selectedPlan === 'pro' ? 'is-selected' : ''}`}
                  onClick={() => setSelectedPlan('pro')}
                >
                  <span className="upgrade-plan-radio" aria-hidden="true" />
                  <div className="upgrade-plan-card-main">
                    <div className="upgrade-plan-card-top">
                      <span className="upgrade-plan-name">{PLANS.pro.name}</span>
                      {isPro ? (
                        <span className="upgrade-plan-pill upgrade-plan-pill--current">{PLANS.basic.currentLabel}</span>
                      ) : (
                        <span className="upgrade-plan-pill upgrade-plan-pill--trial">{PLANS.pro.trialLabel}</span>
                      )}
                    </div>
                    <span className="upgrade-plan-tagline">{PLANS.pro.tagline}</span>
                  </div>
                  <div className="upgrade-plan-price">
                    <span>{proPriceFor().label}</span>
                    <span className="upgrade-plan-period">{PLANS.pro.period}</span>
                  </div>
                </button>
              </div>

              <div className="upgrade-plan-features-panel">
                {selectedPlan === 'pro' && !isPro ? (
                  <div className="upgrade-plan-features-banner">{PRO_TRIAL_DAYS}-day free trial on Pro{proPriceFor().partner ? ` · ${identity.appName} member price` : ''}</div>
                ) : null}
                <h3 className="upgrade-plan-features-title">
                  What&apos;s in {activePlan.name} Plan?
                </h3>
                {selectedPlan === 'pro' ? (
                  <p className="upgrade-plan-features-lead">Everything in Basic and:</p>
                ) : null}
                <ul className="upgrade-plan-features-list">
                  {featureList.map((feature) => (
                    <PlanFeatureRow
                      key={feature.label}
                      {...feature}
                      label={feature.label === 'RiskLo AI' && identity.poweredBy ? `${identity.appName} AI` : feature.label}
                    />
                  ))}
                </ul>
              </div>
            </div>

            {error ? <div className="modal-error">{error}</div> : null}

            <div className="upgrade-plan-footer">
              <p className="upgrade-plan-disclaimer">
                {selectedPlan === 'pro' && !isPro ? (
                  <>
                    New plan starts today and renews each billing cycle until canceled. After the trial ends,
                    you will be charged $20.00 per month starting {trialEndNote}. You can cancel anytime before then.
                  </>
                ) : (
                  <>Basic stays free. Upgrade anytime to unlock Pro risk tools and simulators.</>
                )}
              </p>
              <div className="upgrade-plan-actions">
                <button type="button" className="upgrade-plan-cancel" onClick={onClose}>
                  Cancel
                </button>
                {isPro ? (
                  <button type="button" className="upgrade-button upgrade-button--inline" disabled>
                    Current plan
                  </button>
                ) : selectedPlan === 'pro' ? (
                  <button
                    type="button"
                    className="upgrade-button upgrade-button--inline"
                    onClick={handleUpgrade}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Start Free Trial'}
                  </button>
                ) : (
                  <button type="button" className="upgrade-button upgrade-button--inline" onClick={onClose}>
                    Stay on Basic
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="upgrade-modal-beta-banner">
            Pricing coming soon once beta is complete.
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default UpgradeModal;
